import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Users,
    Plus,
    X,
    Loader2,
    Calendar,
    Hash,
    Copy,
    Check,
} from "lucide-react";
import { useItemDetailsContext } from "./ItemDetailsContext";
import { useDispatch } from "react-redux";
import { updateFolderAssignee, updateLinkAssignee } from "@/redux/actions/folders.action";
import toast from "react-hot-toast";

const ItemDetailsAssigneesSection = () => {
    const {
        itemInfo,
        itemDetails,
        itemType,
        handleAssigneeSelect,
    } = useItemDetailsContext();

    const dispatch = useDispatch();
    const [assigneePopoverOpen, setAssigneePopoverOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [copiedId, setCopiedId] = useState(false);

    // Get initials from name
    const getInitials = (firstName, lastName) => {
        return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
    };

    // Handle assignee add
    const handleAddAssignee = async (userId) => {
        if (itemDetails.employees?.length === itemInfo.users?.length) {
            toast.success("All users are already assigned");
            setAssigneePopoverOpen(false);
            return;
        }

        setIsLoading(true);
        try {
            if (itemType === "folder") {
                await dispatch(updateFolderAssignee(userId, handleAssigneeSelect, itemInfo, false));
            } else {
                await dispatch(updateLinkAssignee(userId, handleAssigneeSelect, itemInfo, false));
            }
        } finally {
            setIsLoading(false);
            // Don't close popover - let user add more assignees
        }
    };

    // Handle assignee remove
    const handleRemoveAssignee = async (userId) => {
        setIsLoading(true);
        try {
            if (itemType === "folder") {
                await dispatch(updateFolderAssignee(userId, handleAssigneeSelect, itemInfo, true));
            } else {
                await dispatch(updateLinkAssignee(userId, handleAssigneeSelect, itemInfo, true));
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Get unassigned users
    const unassignedUsers = itemInfo.users?.filter(
        (user) => !itemDetails.employees?.some((emp) => emp.id === user.id)
    ) || [];

    // Employees are returned from backend in assignment order (oldest first)
    const employees = itemDetails.employees || [];

    const handleCopyId = async () => {
        await navigator.clipboard.writeText(itemDetails.id);
        setCopiedId(true);
        setTimeout(() => setCopiedId(false), 2000);
    };

    const hasMultipleItems = [
        itemDetails.author,
        itemDetails.created_at,
        itemDetails.id,
    ].filter(Boolean).length > 1;

    return (
        <div className="space-y-4 w-full">
            {/* Quick Info Row - Integrated with Assignees Header */}
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                    <div className="flex items-center justify-center size-8 rounded-lg bg-violet-500/10">
                        <Users className="size-4 text-violet-600" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-foreground">Assignees</h3>
                        <p className="text-xs text-muted-foreground">
                            {employees.length} {employees.length === 1 ? "person" : "people"} assigned
                        </p>
                    </div>
                </div>

                {/* Compact Quick Info */}
                <div className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md bg-muted/40 border border-border/50">
                    {/* Author */}
                    {itemDetails.author && (
                        <>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className="flex items-center gap-1.5 cursor-default">
                                        <Avatar className="h-5 w-5 ring-1 ring-background">
                                            <AvatarImage src={itemDetails.author.picture} />
                                            <AvatarFallback className="text-[9px] font-medium bg-primary/10 text-primary">
                                                {itemDetails.author.first_name?.[0]}
                                                {itemDetails.author.last_name?.[0]}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="text-xs font-medium truncate max-w-[80px]">
                                            {itemDetails.author.first_name} {itemDetails.author.last_name}
                                        </span>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Created by {itemDetails.author.first_name} {itemDetails.author.last_name}</p>
                                </TooltipContent>
                            </Tooltip>
                            {hasMultipleItems && (
                                <div className="h-3 w-px bg-border/50" />
                            )}
                        </>
                    )}

                    {/* Created Date */}
                    {itemDetails.created_at && (
                        <>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className="flex items-center gap-1 cursor-default">
                                        <Calendar className="h-3 w-3 text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(itemDetails.created_at).toLocaleDateString("en-US", {
                                                month: "short",
                                                day: "numeric",
                                                year: "numeric",
                                            })}
                                        </span>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>
                                        Created on {new Date(itemDetails.created_at).toLocaleDateString("en-US", {
                                            month: "long",
                                            day: "numeric",
                                            year: "numeric",
                                        })}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                            {itemDetails.id && (
                                <div className="h-3 w-px bg-border/50" />
                            )}
                        </>
                    )}

                    {/* Folder/Link ID with Copy */}
                    {itemDetails.id && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={handleCopyId}
                                    className="flex items-center gap-1.5 px-1.5 py-0.5 rounded hover:bg-background/60 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20"
                                >
                                    <Hash className="h-3 w-3 text-muted-foreground" />
                                    <span className="text-xs font-mono font-medium">
                                        {itemDetails.id}
                                    </span>
                                    {copiedId ? (
                                        <Check className="h-3 w-3 text-emerald-500" />
                                    ) : (
                                        <Copy className="h-3 w-3 text-muted-foreground" />
                                    )}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                {copiedId ? "Copied!" : "Click to copy ID"}
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>
            </div>

            {/* Assignee Chips */}
            <div className="min-h-[52px]">
                <div className="flex flex-wrap items-center gap-2">
                    {employees.length > 0 ? (
                        <>
                            {employees.map((employee) => (
                                <Tooltip key={employee.id}>
                                    <TooltipTrigger asChild>
                                        <div className="group relative inline-flex items-center gap-2 pl-1 pr-2 py-1 rounded-full bg-secondary/50 hover:bg-secondary border border-border/50 transition-colors">
                                            <Avatar className="size-6">
                                                <AvatarImage src={employee.picture} alt={employee.first_name} />
                                                <AvatarFallback className="text-[9px] bg-gradient-to-br from-violet-500 to-purple-600 text-white">
                                                    {getInitials(employee.first_name, employee.last_name)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span className="text-xs font-medium text-foreground max-w-[100px] truncate">
                                                {employee.first_name} {employee.last_name}
                                            </span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveAssignee(employee.id);
                                                }}
                                                disabled={isLoading}
                                                className="size-4 rounded-full flex items-center justify-center hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors"
                                            >
                                                <X className="size-3" />
                                            </button>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom">
                                        <p>{employee.first_name} {employee.last_name}</p>
                                    </TooltipContent>
                                </Tooltip>
                            ))}
                        </>
                    ) : (
                        <span className="text-sm text-muted-foreground">No assignees</span>
                    )}
                    
                    <Popover open={assigneePopoverOpen} onOpenChange={setAssigneePopoverOpen}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-6 px-2 text-xs gap-1 border-dashed"
                                        disabled={isLoading || unassignedUsers.length === 0}
                                    >
                                        {isLoading ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                            <Plus className="h-3 w-3" />
                                        )}
                                        Add Assignee
                                    </Button>
                                </PopoverTrigger>
                            </TooltipTrigger>
                            <TooltipContent>Add or manage assignees</TooltipContent>
                        </Tooltip>
                        <PopoverContent className="w-64 p-0" align="end">
                            <Command>
                                <CommandInput placeholder="Search people..." />
                                <CommandList>
                                    <CommandEmpty>No users found.</CommandEmpty>
                                    <CommandGroup>
                                        {unassignedUsers.map((user) => (
                                            <CommandItem
                                                key={user.id}
                                                value={`${user.first_name} ${user.last_name}`}
                                                onSelect={() => handleAddAssignee(user.id)}
                                                className="flex items-center gap-2.5 cursor-pointer"
                                            >
                                                <Avatar className="size-7">
                                                    <AvatarImage src={user.picture} alt={user.first_name} />
                                                    <AvatarFallback className="text-[10px] bg-gradient-to-br from-violet-500 to-purple-600 text-white">
                                                        {getInitials(user.first_name, user.last_name)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <span className="flex-1 truncate">
                                                    {user.first_name} {user.last_name}
                                                </span>
                                                <Plus className="size-3.5 text-muted-foreground" />
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>
        </div>
    );
};

export default ItemDetailsAssigneesSection;
